/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { SLRFooter, SLRFooterItem } from '@slr/shared-library';
import { sponsorLogo } from 'assets';
import {
  ACCESSIBILITY_PATH,
  CONTACT_PATH,
  EASY_LANGUAGE_PATH,
  IMPRINT_PATH,
  PRIVACY_POLICY_PATH,
  SMART_CITIES_URL,
  TERMS_OF_USE_PATH
} from 'configs';
import { FC } from 'react';
import { Image } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { LanguageSelector } from '../../components/LanguageSelector';

import { APP_NAME } from 'settings';
import './Footer.scss';

const LegalLinks: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'footer'
  });
  return (
    <ul>
      <li>
        <Link
          to={PRIVACY_POLICY_PATH}
          title="zu den Datenschutzinformationen navigieren"
        >
          {t('links.privacyPolicy')}
        </Link>
      </li>
      <li>
        <Link
          to={TERMS_OF_USE_PATH}
          title="zu den Nutzungsbedingungen navigieren"
        >
          {t('links.termsOfUse')}
        </Link>
      </li>
      <li>
        <Link to={IMPRINT_PATH} title="zu den impressum navigieren">
          {t('links.imprint')}
        </Link>
      </li>
      <li>
        <Link to={ACCESSIBILITY_PATH} title="zur Barrierefreiheit navigieren">
          {t('links.accessibility')}
        </Link>
      </li>
      <li>
        <Link to={EASY_LANGUAGE_PATH} title="zur Leichten Sprache navigieren">
          {t('links.easyLanguage')}
        </Link>
      </li>
    </ul>
  );
};

const SupportLinks: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'footer'
  });
  return (
    <>
      <p>
        <span>{t('help.contactUs')}</span>
        <br />
        <span>{t('help.lookingForward')}</span>
      </p>
      <ul>
        <li>
          <Link to={CONTACT_PATH} title="zum Kontakt navigieren">
            {t('help.contact')}
          </Link>
        </li>
      </ul>
    </>
  );
};

const ProjectInfo: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'footer'
  });
  return (
    <>
      <p>{t('project.description', { appName: APP_NAME })}</p>
      <p>
        <Trans
          t={t}
          i18nKey="project.slr"
          components={[
            <a
              href={SMART_CITIES_URL}
              target="_blank"
              key="SMART_CITIES_URL"
              title={`zu ${t('project.slrTitle')} navigieren`}
            />
          ]}
        />
      </p>

      <Image
        src={sponsorLogo}
        alt="Logo der Förderorganisation"
        title="Bundesministerium für Wohnen, Stadtentwicklung und Bauwesen"
        width={600}
      />
    </>
  );
};

export const Footer: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'footer'
  });

  const footerItemsList = [
    {
      key: 0,
      title: 'language.title' as const,
      col: 3,
      component: () => (
        <>
          <div className="footer-language-container">
            <LanguageSelector showLabel={false} />
          </div>
          <div className="mt-5">
            <h4>{t('links.title')}</h4>
            <LegalLinks />
          </div>
        </>
      )
    },
    {
      key: 1,
      title: 'help.title' as const,
      col: 4,
      component: SupportLinks
    },
    {
      key: 2,
      title: 'project.title' as const,
      col: 5,
      component: ProjectInfo
    }
  ];

  return (
    <footer>
      <div className="footer-wrapper">
        <SLRFooter className="footer">
          {footerItemsList.map((item) => {
            const title = t(item.title);
            return (
              <SLRFooterItem
                key={item.key}
                title={typeof title === 'string' ? title : String(title)}
                col={item.col}
              >
                <item.component />
              </SLRFooterItem>
            );
          })}
        </SLRFooter>
      </div>
    </footer>
  );
};
